import { useCallback, useState } from "react";
import { mapAuthErrorToMessage } from "./authErrorMessages";
import type { LoginFormValues } from "./useLoginForm";
import type { LoginTr } from "./useLoginTranslations";

interface LoginAuthApi {
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  resendEmailVerification: () => Promise<"sent" | "alreadyVerified">;
}

export type MessageState = {
  type: "success" | "error" | "info";
  text: string;
} | null;

export function useLoginActions(
  auth: LoginAuthApi,
  getFormValues: () => LoginFormValues,
  tr: LoginTr,
  setError: (msg: string) => void,
) {
  const [authLoading, setAuthLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<"google" | "microsoft" | null>(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState<MessageState>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<MessageState>(null);

  const handleEmailPasswordSignIn = useCallback(async () => {
    setError("");
    setAuthLoading(true);
    try {
      const { email, password } = getFormValues();
      await auth.login(email, password);
    } catch (err) {
      console.error("Email login error:", err);
      setError(mapAuthErrorToMessage(err, (k, f) => tr(k, f), "signIn"));
    } finally {
      setAuthLoading(false);
    }
  }, [auth, getFormValues, tr, setError]);

  const handleEmailPasswordSignUp = useCallback(async () => {
    const { email, password, confirmPassword } = getFormValues();
    if (password !== confirmPassword) {
      setError(tr("login.errors.passwordsDoNotMatch", "Passwords do not match."));
      return;
    }
    setError("");
    setAuthLoading(true);
    try {
      await auth.signUp(email, password);
    } catch (err) {
      console.error("Email sign up error:", err);
      setError(mapAuthErrorToMessage(err, (k, f) => tr(k, f), "signUp"));
    } finally {
      setAuthLoading(false);
    }
  }, [auth, getFormValues, tr, setError]);

  const handleGoogleSignIn = useCallback(async () => {
    setError("");
    setSocialLoading("google");
    try {
      await auth.loginWithGoogle();
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError(tr("login.errors.googleSignInFailed", "Google sign-in failed"));
    } finally {
      setSocialLoading(null);
    }
  }, [auth, setError, tr]);

  const handleMicrosoftSignIn = useCallback(async () => {
    setError("");
    setSocialLoading("microsoft");
    try {
      await auth.loginWithMicrosoft();
    } catch (err) {
      console.error("Microsoft sign-in error:", err);
      setError(tr("login.errors.microsoftSignInFailed", "Microsoft sign-in failed. Please try again."));
    } finally {
      setSocialLoading(null);
    }
  }, [auth, setError, tr]);

  const handleForgotPassword = useCallback(async () => {
    const email = getFormValues().email?.trim();
    if (!email || !/^\S+@\S+$/.test(email)) {
      setResetPasswordMessage({
        type: "error",
        text: tr("login.forgotPassword.invalidEmail", "Enter your email address above first."),
      });
      return;
    }
    setResetPasswordMessage(null);
    setResetPasswordLoading(true);
    try {
      await auth.sendPasswordResetEmail(email);
      setResetPasswordMessage({
        type: "success",
        text: tr("login.forgotPassword.sent", "Check your email for a link to reset your password."),
      });
    } catch (err) {
      console.error("Password reset error:", err);
      setResetPasswordMessage({
        type: "error",
        text: mapAuthErrorToMessage(err, (k, f) => tr(k, f), "passwordReset"),
      });
    } finally {
      setResetPasswordLoading(false);
    }
  }, [auth, getFormValues, tr]);

  const clearResetPasswordMessage = useCallback(() => {
    setResetPasswordMessage(null);
  }, []);

  const handleResendVerification = useCallback(async () => {
    setResendMessage(null);
    setResendLoading(true);
    try {
      const result = await auth.resendEmailVerification();
      if (result === "sent") {
        setResendMessage({
          type: "success",
          text: tr("login.verifyEmail.sent", "Verification email sent."),
        });
      } else {
        setResendMessage({
          type: "info",
          text: tr(
            "login.verifyEmail.alreadyVerified",
            "Your email is already confirmed. Updating your account status now.",
          ),
        });
      }
    } catch (e) {
      setResendMessage({
        type: "error",
        text: mapAuthErrorToMessage(e, (k, f) => tr(k, f), "resend"),
      });
    } finally {
      setResendLoading(false);
    }
  }, [auth, tr]);

  return {
    handleEmailPasswordSignIn,
    handleEmailPasswordSignUp,
    handleGoogleSignIn,
    handleMicrosoftSignIn,
    handleForgotPassword,
    clearResetPasswordMessage,
    handleResendVerification,
    authLoading,
    socialLoading,
    resetPasswordLoading,
    resetPasswordMessage,
    resendLoading,
    resendMessage,
  };
}
